/* eslint-disable react/jsx-max-depth */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import routes from '../../routes/constants';
import text, { formatCurrency } from '../../text';

import { pickBy, values } from 'lodash';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { MapViewContext } from '../../components/mapView/mapViewContext';
import { profileSelectors } from '../../redux/selectors/profile';
import { isJobPayable } from '../../utilities/job';
import { isMembershipPriceId } from '../../utilities/membership';
import { isRouteIncluded } from '../../utilities/routes';
import {
  isStaff,
  isUserGovernment,
  isUserUnderTrial,
} from '../../utilities/user';
import Loading from '@/components/loading/Loading';
import Icon from '@/components/icon/Icon';
import classNames from 'classnames';
import { useAppSelector } from '@/hooks/useAppSelector';
import { DEFAULT_CURRENCY } from '@/constants/price';

const WIZARD_KEYS = {
  order: 'order',
  membership: 'membership',
};

const PaymentStatusOverlay = ({ paymentStatus, onRetry }) => {
  const membershipPriceId = useAppSelector(
    (state) => state.order.membershipPriceId
  );

  const paymentMethodId = useAppSelector(
    (state) => state.order.paymentMethodId
  );
  const membershipPrices = useAppSelector(
    (state) => state.profileReducer.membershipPrices
  );

  const selectedMembership = membershipPrices.value.find(
    (m) => m.id === membershipPriceId
  );
  const totalPriceFormatted = formatCurrency(
    selectedMembership?.grand_total,
    DEFAULT_CURRENCY.name,
    DEFAULT_CURRENCY.scale_factor,
    {},
    true
  );

  return (
    <div className='d-flex flex-column justify-content-center align-items-center w-100'>
      <div
        className={classNames('WhiteContainer', {
          isPaymentProcessing: paymentStatus === 'processing',
          isPaymentError: paymentStatus === 'error',
        })}
      >
        {paymentStatus === 'processing' && (
          <>
            <p className='mb-0 font-18 text-center font-weight--600'>
              {text('membershipProcessingHeading')}
            </p>
            <p className='mb-0 font-14 text-center font-weight--400'>
              {text('membershipProcessingSubheading')}
            </p>
            <br />
            <br />
            <Loading />
          </>
        )}

        {paymentStatus === 'error' && (
          <div className='d-flex flex-column justify-content-center'>
            <div className='d-flex flex-column align-items-center mb-2'>
              <Icon icon='circled-exclamation' className='mb-3' />
              <p className='mb-0 font-24 text-center font-weight--600 px-5'>
                {text('paymentFailed')}
              </p>
            </div>
            <div className='divider my-3' />
            <div className='d-flex flex-row justify-content-between'>
              <p className='mb-0 font-18'>{text('membership')}</p>
              <p className='mb-0 font-18'>{totalPriceFormatted}</p>
            </div>
            <div className='divider my-3' />

            <div className='d-flex flex-row justify-content-center'>
              <a
                href={`${process.env.LARKI_MARKETING_URL}/contact-us`}
                target='_blank'
                className='mb-0 font-14 text-right'
              >
                {text('contactLarkiSupport')}
              </a>
              <p className='mb-0 px-2 font-14'>|</p>
              <a
                href='#'
                className='mb-0 font-14 text-left white-underlined-link'
                onClick={onRetry}
              >
                {text('tryAgain')}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PaymentStatusOverlay.propTypes = {
  paymentStatus: PropTypes.oneOf(['processing', 'error']),
  onRetry: PropTypes.func.isRequired,
};

export const PaymentContext = createContext(null);

const PaymentProvider = ({ children, job }) => {
  const [activeWizardStep, setActiveWizardStep] = useState(null);
  const [cardHeading, setCardHeading] = useState(text('estimate'));
  const [paymentStatus, setPaymentStatus] = useState(null); // error, processing, success, null
  const location = useLocation();
  const history = useHistory();
  const isInOrderPage = isRouteIncluded([routes.mp.payment()]);
  const isInMembershipPage = isRouteIncluded([
    routes.user.membershipManagement,
  ]);
  const { currentUser, membershipPriceId } = useSelector((state) => ({
    currentUser: state.profileReducer.userProfile,
    membershipPriceId: state.order.membershipPriceId,
  }));

  const hasValidMembership = useSelector(profileSelectors.hasValidMembership);
  const wizardStepsHeading = useMemo(
    () => ({
      [WIZARD_KEYS.order]: [
        {
          text: text('estimate'),
          filter: () => {
            return (
              !process.env.DISABLE_STRIPE &&
              !isUserGovernment(currentUser) &&
              !isStaff(currentUser.role) &&
              (!hasValidMembership || isUserUnderTrial(currentUser))
            );
          },
        }, // membership
        {
          text: text('estimate'),
        }, // project
        {
          text: text('quote'),
          filter: () =>
            (!isStaff(currentUser.role) && isJobPayable(job)) ||
            isMembershipPriceId(membershipPriceId),
        }, // payment
        {
          text: text('invoice'),
        }, // confirmation
      ],
      [WIZARD_KEYS.membership]: [
        {
          text: text('estimate'),
        },
        {
          text: text('quote'),
        },
        {
          text: text('invoice'),
        },
      ],
    }),
    [currentUser, hasValidMembership, membershipPriceId, job]
  );

  const wizardKeys = useMemo(
    () => [
      {
        key: WIZARD_KEYS.order,
        isActive: isInOrderPage(location.pathname),
      },
      {
        key: WIZARD_KEYS.membership,
        isActive: isInMembershipPage(location.pathname),
      },
    ],
    [isInMembershipPage, isInOrderPage, location.pathname]
  );

  const getActiveRouteKey = useMemo(() => {
    if (!activeWizardStep) {
      return text('estimate');
    }

    return wizardKeys.find((wk) => !!wk.isActive);
  }, [activeWizardStep, wizardKeys]);

  const handleSetCardHeading = useCallback(() => {
    if (activeWizardStep) {
      const { step, wizard } = activeWizardStep;
      const filteredWizardStepsHeading = values(
        pickBy(wizardStepsHeading[wizard], (step) => {
          return !step.filter || step.filter();
        })
      );
      // (step - 1) if wizard = order coz `Cart` step is index 0 so step is starting at 1
      const stepIndex = wizard === WIZARD_KEYS.membership ? step : step - 1;
      setCardHeading(
        filteredWizardStepsHeading?.[`${stepIndex}`]?.text || text('estimate')
      );
    }
  }, [activeWizardStep, wizardStepsHeading]);

  const handleSetWizardStep = useCallback(
    (step) => {
      setActiveWizardStep({ step, wizard: getActiveRouteKey?.key });
    },
    [getActiveRouteKey?.key]
  );

  const memoizedState = useMemo(() => {
    const membershipStepper = [
      text('member'),
      text('paymentMethod'),
      text('confirmation'),
    ];
    return {
      activeWizardStep,
      cardHeading,
      membershipStepper,
      paymentStatus,
    };
  }, [activeWizardStep, cardHeading, paymentStatus]);

  const memoizedActions = useMemo(
    () => ({
      handleSetWizardStep,
      setPaymentStatus,
    }),
    [handleSetWizardStep, setPaymentStatus]
  );

  useEffect(() => {
    handleSetCardHeading();
  }, [handleSetCardHeading]);

  const retryPayment = useCallback(
    (e) => {
      e.preventDefault();
      const params = new URLSearchParams(location.search);
      params.set('step', '1');
      const search = params.toString();
      history.replace({
        ...location,
        search: search ? `?${search}` : '',
      });
      setPaymentStatus(null);
    },
    [location.pathname, location.search, history, setPaymentStatus]
  );

  // ref to expose functions from Stepper component
  const wizzardFunctions = useRef({});
  return (
    <PaymentContext.Provider
      value={{
        states: memoizedState,
        actions: memoizedActions,
        refs: {
          wizzardFunctions,
        },
      }}
    >
      {paymentStatus ? (
        <PaymentStatusOverlay
          paymentStatus={paymentStatus}
          onRetry={retryPayment}
        />
      ) : (
        children
      )}
    </PaymentContext.Provider>
  );
};

PaymentProvider.propTypes = {
  children: PropTypes.element,
};

export default PaymentProvider;
